import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { readCookie, writeCookie } from "@/lib/cookie";

/**
 * Theme system for the portal.
 *
 *   - "light"  → force light tokens
 *   - "dark"   → force dark tokens
 *   - "system" → follow `prefers-color-scheme`
 *
 * Tailwind 4 picks up the `.dark` class via the `@custom-variant dark`
 * declaration in `@pontimed/ui/styles/globals.css`. The bootstrap script
 * in `index.html` reads the same cookie BEFORE React mounts, so the right
 * `<html class="dark|light">` is set on first paint (no FOUC).
 *
 * Storage: cookie `pontimed_theme` scoped to `.pontimed.app` /
 * `.pontimed.local` so the choice survives the marketing ↔ portal hop
 * without a round-trip to the server.
 */

const COOKIE_NAME = "pontimed_theme";

export type Theme = "light" | "dark" | "system";
export type ResolvedTheme = "light" | "dark";

interface ThemeContextValue {
  /** User-selected theme. */
  theme: Theme;
  /** Effective theme after resolving "system" against the OS preference. */
  resolvedTheme: ResolvedTheme;
  /** Persists + applies the new theme. */
  setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function readStoredTheme(): Theme {
  const raw = readCookie(COOKIE_NAME);
  if (raw === "light" || raw === "dark" || raw === "system") return raw;
  return "system";
}

function resolveSystemTheme(): ResolvedTheme {
  if (typeof window === "undefined") return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function applyTheme(resolved: ResolvedTheme) {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  // Keep `.dark` and `.light` mutually exclusive on <html>. The pre-React
  // bootstrap script in index.html sets one of these, and the high-specificity
  // CSS there (`html.dark body { bg dark }`, `html:not(.light) body { bg dark }`
  // under `prefers-color-scheme: dark`) will override Tailwind's
  // `body { @apply bg-background }` if the matching class is missing — leaving
  // the body stuck dark when the user picks light on a dark-OS, and vice versa.
  if (resolved === "dark") {
    root.classList.add("dark");
    root.classList.remove("light");
  } else {
    root.classList.add("light");
    root.classList.remove("dark");
  }
  root.style.colorScheme = resolved;
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(() => readStoredTheme());
  const [systemTheme, setSystemTheme] = useState<ResolvedTheme>(() => resolveSystemTheme());

  const resolvedTheme: ResolvedTheme = theme === "system" ? systemTheme : theme;

  // Apply on every change. Effect (not layout-effect) is fine — the
  // bootstrap script in index.html already paints the right class before
  // React mounts, so this is just for runtime swaps.
  useEffect(() => {
    applyTheme(resolvedTheme);
  }, [resolvedTheme]);

  // React to OS-level prefers-color-scheme changes when in "system" mode.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = (e: MediaQueryListEvent) => setSystemTheme(e.matches ? "dark" : "light");
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);

  const setTheme = useCallback((next: Theme) => {
    setThemeState(next);
    writeCookie(COOKIE_NAME, next);
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({ theme, resolvedTheme, setTheme }),
    [theme, resolvedTheme, setTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    // Fallback so a stale import never crashes the page; just renders light.
    return { theme: "system", resolvedTheme: "light", setTheme: () => {} };
  }
  return ctx;
}
